import { useQuery } from '@apollo/client';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import BlockRounded from '@mui/icons-material/BlockRounded';
import HowToRegOutlined from '@mui/icons-material/HowToRegOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import { alpha, Avatar, Box, Chip, IconButton, LinearProgress, Link, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';

import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ErrorNotifier } from '../../common/error/ErrorNotifier';
import { Popover } from '../../common/popover/Popover';
import { Table } from "../../common/table/Table";
import { EditUser } from './EditUser';
import { columns } from './user';
import { Filter, isFiltering, UserFilters } from './UserFilters';
import { USERS } from './users.queries';
import { DownloadCsvButton } from '../../common/DownloadCsvButton';
import { config } from '../../config';
import { BanMemberDialog } from '../userBans/BanMemberDialog';
import { useSetUserBanned } from '../userBans/useSetUserBanned';
import { banColumnLabel, BanTarget } from '../userBans/userBan';
import { initialsOf } from '../userDetails/userDetailsModel';

const perPage = config.rowsPerPage;

// The name is a real link, so the page can be reached by keyboard and opened in
// a new tab. The rest of the row opens it too (`onRowClick`). It stays in view
// while the columns between it and the actions scroll sideways.
const tableColumns = columns.map((column) => {
    if (column.key === 'username') {
        return {
            ...column,
            sticky: 'left' as const,
            rowRender: (row: any) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Avatar
                        aria-hidden
                        sx={(theme) => ({
                            display: { xs: 'none', sm: 'flex' },
                            width: 30,
                            height: 30,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                        })}
                    >
                        {initialsOf(row)}
                    </Avatar>
                    <Link
                        component={RouterLink}
                        to={`/users/${row.id}`}
                        underline="hover"
                        sx={{
                            display: 'block',
                            fontWeight: 600,
                            maxWidth: { xs: 96, sm: 200 },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {row.username || `User #${row.id}`}
                    </Link>
                </Box>
            ),
        };
    }
    if (column.key === 'isBanned') {
        return {
            ...column,
            rowRender: (row: any) =>
                row.isBanned ? <Chip size="small" color="error" variant="outlined" label={banColumnLabel(row)} /> : '—',
        };
    }
    return column;
});

export const Users: React.FC<any> = (): JSX.Element => {
    const [filter, setFilter] = useState<any>(undefined);
    const [filtering, setFiltering] = useState(false);
    const [sortBy, setSortBy] = React.useState<any>(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(perPage);

    // Everything the list depends on goes in the variables, so changing page or
    // sort keeps the filter, and the CSV below exports what is on screen.
    const { data, previousData, loading, error, refetch } = useQuery(USERS, {
        variables: {
            pagination: { take: rowsPerPage, skip: page * rowsPerPage },
            ...(sortBy ? { orderBy: sortBy } : {}),
            ...(filter ? { filter } : {}),
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const list = (data ?? previousData)?.fetchUsers;

    const [userToEdit, setUserToEdit] = useState<any>(null);
    const [isEditUserOpen, setIsEditUserOpen] = useState(false);

    // Banning lives here because this is where an admin can actually find the
    // person. The ban screen owns the other half — who is banned, and un-banning
    // them — and it is the only place a *deleted* account can be listed.
    const [banTarget, setBanTarget] = useState<BanTarget | null>(null);
    const { setBanned, saving: savingBan } = useSetUserBanned();

    const editFn = useCallback((item: any) => {
        setUserToEdit(item);
        setIsEditUserOpen(true);
    }, []);

    const navigate = useNavigate();

    const goToDetails = useCallback((item: any) => {
        navigate(`/users/${item.id}`);
    }, [navigate]);

    const onFilter = useCallback((next: Filter) => {
        setFilter({
            fieldFilter: {
                filters: next.filters,
                operator: next.operator,
            },
            hasReferred: next.usersThatHaveReferred,
            hasBeenReferred: next.usersThatHaveBeenReferred,
            hasRecommendedABusiness: next.usersThatHaveRecommended,
            hasRecommendedSameBusiness: next.usersThatHaveRecommendedSameBusiness,
            visitedPage: next.visitedPage,
            hasSharedReferralLink: next.hasSharedReferralLink,
            referredByUser: next.referredByUser,
        });
        setFiltering(isFiltering(next));
        // The page being read may not exist in a shorter list.
        setPage(0);
    }, []);

    const handleSort = useCallback((sorting: any) => {
        setSortBy(sorting);
    }, []);

    const openBanDialog = useCallback((row: any) => {
        setBanTarget({
            // `fetchUsers` returns `id` as a GraphQL ID, so it arrives as a
            // string; `setUserBanned` takes an Int and rejects the string.
            userId: Number(row.id),
            username: row.username,
            email: row.email,
            phone: row.phone,
            isBanned: Boolean(row.isBanned),
        });
    }, []);

    const confirmBan = useCallback(async (reason?: string) => {
        const target = banTarget;
        if (!target) return;

        setBanTarget(null);

        // The state to set, not a flip — a retry after a timeout cannot land the
        // account in the opposite state.
        const status = await setBanned(target, !target.isBanned, reason);

        // Refetched rather than patched: the row stays in this list either way
        // (no `isDeleted` filter here), so a re-read is the simplest way to show
        // the new state, and this query is not cached server-side.
        if (status) refetch();
    }, [banTarget, refetch, setBanned]);

    const total = list?.total ?? 0;
    const countText = filtering
        ? `${total.toLocaleString('en-US')} ${total === 1 ? 'user matches' : 'users match'} these filters`
        : `${total.toLocaleString('en-US')} ${total === 1 ? 'user' : 'users'}`;

    return (
        <Stack spacing={3}>
            <Popover
                open={isEditUserOpen}
                handleClose={() => {
                    setIsEditUserOpen(false);
                }}
            >
                <EditUser
                    user={userToEdit}
                    handleClose={(isSuccess: number | undefined) => {
                        if (isSuccess) {
                            refetch();
                        }
                        setIsEditUserOpen(false);
                    }}
                />
            </Popover>
            <BanMemberDialog
                open={Boolean(banTarget)}
                target={banTarget}
                saving={savingBan}
                onCancel={() => setBanTarget(null)}
                onConfirm={confirmBan}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                    <Typography variant="h4" component="h1">Users</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, minHeight: 20 }}>
                        {list ? countText : ''}
                    </Typography>
                </Box>
                <DownloadCsvButton
                    path="user/export-csv"
                    filter={{ filter }}
                    variant="outlined"
                    sx={{}}
                    startIcon={<DownloadRounded />}
                />
            </Box>

            <Paper component="section" aria-label="Filters" variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                <UserFilters onFilter={onFilter} />
            </Paper>

            <ErrorNotifier error={error} />

            <Paper component="section" aria-label="Users" aria-busy={loading} variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ height: 2 }}>{loading && list && <LinearProgress sx={{ height: 2 }} />}</Box>
                {list ? (
                    <Table
                        compact
                        emptyText={filtering ? 'No users match these filters.' : 'No users yet.'}
                        page={page}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        onSort={handleSort}
                        onRowClick={goToDetails}
                        loading={loading}
                        columns={[
                            ...tableColumns,
                            {
                                label: 'Actions',
                                key: 'actions',
                                textAlign: 'right',
                                minWidth: '116px',
                                sticky: 'right',
                                // Not a sortable field — the column is buttons.
                                skipSorting: true,
                                rowRender: (row: any) => {
                                    const banLabel = row.isBanned ? 'Un-ban' : 'Ban';
                                    return (
                                        <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                                            <Tooltip title="Open user page">
                                                <IconButton size="small" component={RouterLink} to={`/users/${row.id}`} aria-label="Open user page">
                                                    <VisibilityOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" aria-label="Edit" onClick={() => editFn(row)}>
                                                    <EditOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={banLabel}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={banLabel}
                                                        color={row.isBanned ? 'primary' : 'error'}
                                                        disabled={savingBan}
                                                        onClick={() => openBanDialog(row)}
                                                    >
                                                        {row.isBanned ? <HowToRegOutlined fontSize="small" /> : <BlockRounded fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    );
                                },
                            },
                        ]}
                        rows={list.users}
                        total={list.total}
                    />
                ) : (
                    loading && (
                        <Box role="status" aria-label="Loading users" sx={{ p: 2, display: 'grid', gap: 1.5 }}>
                            {Array.from({ length: 8 }, (_, index) => (
                                <Skeleton key={index} variant="rounded" height={32} />
                            ))}
                        </Box>
                    )
                )}
            </Paper>
        </Stack>
    );
};
